import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, TooltipProps } from "recharts";
import { CHART_COLORS, CHART_SEQUENCE } from "./chartTokens";
import { formatDecimal } from "@/utils";

type ChannelData = {
  source: string;
  totalLeads: string;
};

type ChannelDistributionCardProps = {
  data: ChannelData[];
  title?: string;
  subtitle?: string;
  colSpan?: number;
};

const COLORS = CHART_SEQUENCE;

// Friendly names for common channels (Spanish UI)
const channelNames: Record<string, string> = {
  website: "Sitio web",
  whatsapp: "WhatsApp",
  phone: "Teléfono",
  email: "Correo",
  facebook: "Facebook",
  instagram: "Instagram",
  referral: "Referido",
  unknown: "Desconocido",
  dashboard: "Panel",
  campaign: "Campaña",
  direct: "Directo",
};

// Custom tooltip component
const CustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    const tooltipValue = payload[0].value ?? 0;
    const tooltipName = payload[0].name ?? "";
    const totalLeads = payload[0].payload.totalLeads ?? 0;
    const percentage = totalLeads > 0 ? (tooltipValue / totalLeads) * 100 : 0;

    return (
      <div className="rounded-md border border-border bg-card p-3 shadow-md">
        <p className="text-sm font-semibold text-palomma-primary">{tooltipName}</p>
        <p className="text-sm font-medium text-palomma-secondary">
          {tooltipValue} contactos ({formatDecimal(percentage, 1)}%)
        </p>
      </div>
    );
  }
  return null;
};

// Custom label renderer to prevent overlap
const renderCustomLabel = (props: any) => {
  const { cx, cy, midAngle, outerRadius, name, percent } = props;
  // Calculate the position of the label to be outside the pie
  const radius = outerRadius * 1.2;
  const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
  const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);

  // Format percentage to avoid long decimals
  const formattedPercent = `${formatDecimal(percent * 100, 0)}%`;

  return (
    <text
      x={x}
      y={y}
      fill={CHART_COLORS.axis}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize="12px"
      fontWeight="500"
    >
      {`${name}: ${formattedPercent}`}
    </text>
  );
};

const ChannelDistributionCard = ({
  data,
  title = "Distribución por canal",
  subtitle = "Origen de contactos",
  colSpan = 1,
}: ChannelDistributionCardProps) => {
  // Calculate total leads first
  const totalLeads = data.reduce((sum, item) => sum + parseInt(item.totalLeads || "0"), 0);

  // Transform data for the pie chart
  const chartData = data.map((item, index) => ({
    name: channelNames[item.source.toLowerCase()] || item.source,
    value: parseInt(item.totalLeads),
    color: COLORS[index % COLORS.length],
    totalLeads,
  }));

  return (
    <div style={{ gridColumn: `span ${colSpan}` }}>
      <div className="h-full rounded-2xl border border-border bg-card p-6">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-palomma-secondary">{title}</h3>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>

        <div className="h-[260px] md:h-[320px]">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  labelLine={false}
                  label={renderCustomLabel}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{ paddingTop: "20px" }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-muted-foreground">No hay datos disponibles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChannelDistributionCard;
